/**
 * 协议错误码中文文案映射。
 *
 * 该文件只维护前端展示文案，不修改 proto 生成物；
 * 新增协议 Errno 后，测试会要求这里同步补齐中文提示。
 */

import { Errno } from '@puppycad/proto';

/** 未识别协议错误码时的通用兜底文案。 */
export const DEFAULT_ERRNO_CHINESE_MESSAGE = '发生未知错误，请稍后重试。';

/**
 * 协议 Errno 到中文文案的完整映射。
 *
 * 文案以“用户能理解下一步发生了什么”为目标；
 * 具体业务页面如果需要更细的操作建议，可以在此结果之上追加上下文。
 */
export const ERRNO_CHINESE_MESSAGE_MAP = {
  // 与 gRPC status code 对齐的通用错误。
  [Errno.ERRNO_OK]: '操作成功。',
  [Errno.ERRNO_CANCELLED]: '操作已取消。',
  [Errno.ERRNO_UNKNOWN]: '未知错误。',
  [Errno.ERRNO_INVALID_ARGUMENT]: '请求参数无效。',
  [Errno.ERRNO_DEADLINE_EXCEEDED]: '请求超时，请稍后重试。',
  [Errno.ERRNO_NOT_FOUND]: '请求的资源不存在。',
  [Errno.ERRNO_ALREADY_EXISTS]: '资源已存在。',
  [Errno.ERRNO_PERMISSION_DENIED]: '权限不足，无法执行该操作。',
  [Errno.ERRNO_RESOURCE_EXHAUSTED]: '资源已耗尽，请稍后重试。',
  [Errno.ERRNO_FAILED_PRECONDITION]: '当前状态不满足操作条件。',
  [Errno.ERRNO_ABORTED]: '操作已中止，请重试。',
  [Errno.ERRNO_OUT_OF_RANGE]: '请求范围超出允许范围。',
  [Errno.ERRNO_UNIMPLEMENTED]: '当前功能尚未实现。',
  [Errno.ERRNO_INTERNAL]: '服务内部错误，请稍后重试。',
  [Errno.ERRNO_UNAVAILABLE]: '服务暂时不可用，请稍后重试。',
  [Errno.ERRNO_DATA_LOSS]: '数据损坏或丢失。',
  [Errno.ERRNO_UNAUTHENTICATED]: '登录状态无效，请重新登录。',

  // 服务与基础设施错误。
  [Errno.ERRNO_UNEXPECTED]: '发生非预期错误，请稍后重试。',
  [Errno.ERRNO_TIMEOUT]: '操作超时，请稍后重试。',
  [Errno.ERRNO_DEPENDENCY_FAILED]: '依赖服务调用失败，请稍后重试。',
  [Errno.ERRNO_DB_ERROR]: '数据库操作失败。',
  [Errno.ERRNO_CACHE_ERROR]: '缓存系统异常。',
  [Errno.ERRNO_MQ_ERROR]: '消息队列服务异常。',
  [Errno.ERRNO_BAD_GATEWAY]: '下游网关异常。',
  [Errno.ERRNO_RATE_LIMITED]: '请求过于频繁，请稍后重试。',
  [Errno.ERRNO_QUOTA_EXCEEDED]: '配额已超出。',
  [Errno.ERRNO_SERVICE_MAINTENANCE]: '服务维护中，请稍后再试。',
  [Errno.ERRNO_NOT_SUPPORTED]: '当前操作不受支持。',
  [Errno.ERRNO_CONFIG_ERROR]: '服务配置错误。',
  [Errno.ERRNO_AUTHORIZATION_FAILED]: '授权失败，请确认账号权限。',
  [Errno.ERRNO_SIGNATURE_INVALID]: '签名无效，请重新发起请求。',

  // 业务错误。
  [Errno.ERRNO_2FA_REQUIRED]: '需要完成双因子验证。',
  [Errno.ERRNO_USER_NOT_FOUND]: '用户不存在。',
  [Errno.ERRNO_USER_PASSWORD_INVALID]: '用户名或密码无效。',
  [Errno.ERRNO_AUTH_TOKEN_EXPIRED]: '登录状态已过期，请重新登录。',
  [Errno.ERRNO_FILE_TOO_LARGE]: '上传文件过大。',
  [Errno.ERRNO_FILE_NOT_FOUND]: '文件不存在。',
  [Errno.ERRNO_PAYMENT_REQUIRED]: '需要完成支付后继续。',
  [Errno.ERRNO_ACCOUNT_LOCKED]: '账号已被锁定。',
  [Errno.ERRNO_ACCOUNT_SUSPENDED]: '账号已被暂停使用。',
  [Errno.ERRNO_EMAIL_ALREADY_EXISTS]: '邮箱已被注册。',
  [Errno.ERRNO_USERNAME_TAKEN]: '用户名已被占用。',
  [Errno.ERRNO_CAPTCHA_REQUIRED]: '需要完成验证码验证。',
  [Errno.ERRNO_CAPTCHA_INVALID]: '验证码无效。',
  [Errno.ERRNO_RATE_LIMIT_EXCEEDED]: '超过速率限制，请稍后重试。',
  [Errno.ERRNO_PAYMENT_FAILED]: '支付失败。',
  [Errno.ERRNO_INSUFFICIENT_FUNDS]: '余额不足。',
  [Errno.ERRNO_PASSWORD_CHANGE_REQUIRED]: '需要修改密码后继续。',
  [Errno.ERRNO_2FA_SETUP_REQUIRED]: '需要先启用或绑定双因子验证。',
} satisfies Record<Errno, string>;

/**
 * 判断输入值是否是当前协议已知的 Errno。
 *
 * @param value 待判断的错误码值。
 * @returns 是否为已知协议错误码。
 */
export function isKnownErrno(value: unknown): value is Errno {
  // 对象键在运行时会被转成字符串，因此必须使用 hasOwnProperty 做稳定判断。
  return typeof value === 'number' &&
    Object.prototype.hasOwnProperty.call(ERRNO_CHINESE_MESSAGE_MAP, value);
}

/**
 * 解析协议错误码对应的中文文案。
 *
 * @param errno 协议错误码。
 * @returns 已知错误码的中文文案；未知错误码返回通用兜底文案。
 */
export function resolveErrnoChineseMessage(errno: Errno): string {
  if (!isKnownErrno(errno)) {
    return DEFAULT_ERRNO_CHINESE_MESSAGE;
  }

  return ERRNO_CHINESE_MESSAGE_MAP[errno];
}
